// Scrollable message timeline for one conversation; auto-scrolls to the
// newest message unless the reader has scrolled up to look at history.

import { useLayoutEffect, useRef } from 'react'
import type { ChatMessage } from '@goblic/shared'
import { EmptyState } from '../ui'
import { MessageItem, ToolGroup } from './message-item'

type Entry = { kind: 'message'; message: ChatMessage } | { kind: 'tools'; messages: ChatMessage[] }

/** Collapse runs of tool messages into one entry. Assistant rows that will render nothing don't break a run. */
function groupMessages(messages: ChatMessage[]): Entry[] {
  const entries: Entry[] = []
  for (const message of messages) {
    if (message.role === 'tool') {
      const last = entries[entries.length - 1]
      if (last?.kind === 'tools') last.messages.push(message)
      else entries.push({ kind: 'tools', messages: [message] })
      continue
    }
    const streaming = (message.payload as { streaming?: boolean } | null)?.streaming === true
    if (message.role === 'assistant' && !streaming && !message.content.trim()) continue
    entries.push({ kind: 'message', message })
  }
  return entries
}

export function MessageList({
  conversationId,
  messages,
  waitingForRunner,
}: {
  conversationId: string
  messages: ChatMessage[]
  /** The active turn is queued/running but no runner has picked it up yet (no worker built yet). */
  waitingForRunner: boolean
}) {
  const bottomRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const previousLayout = useRef<{ conversationId: string; firstSeq?: string; height: number } | null>(null)
  useLayoutEffect(() => {
    const container = containerRef.current
    if (!container) return
    const previous = previousLayout.current
    const firstSeq = messages[0]?.seq
    const prepended = previous?.firstSeq && firstSeq && BigInt(firstSeq) < BigInt(previous.firstSeq)
    if (!previous || previous.conversationId !== conversationId) {
      container.scrollTop = container.scrollHeight
    } else if (prepended) {
      // Keep the currently read message in place when an older page is inserted.
      container.scrollTop += container.scrollHeight - previous.height
    } else if (previous.height - container.scrollTop - container.clientHeight < 200) {
      bottomRef.current?.scrollIntoView({ behavior: 'auto' })
    }
    previousLayout.current = { conversationId, firstSeq, height: container.scrollHeight }
  }, [messages, waitingForRunner, conversationId])

  if (messages.length === 0 && !waitingForRunner) {
    return (
      <EmptyState title="Say hello" art="empty">
        Ask about your projects, workflows, roles or agents — the assistant
        acts with your own permissions.
      </EmptyState>
    )
  }

  return (
    <div ref={containerRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
      {groupMessages(messages).map((entry) =>
        entry.kind === 'tools' ? (
          <ToolGroup key={entry.messages[0].id} messages={entry.messages} />
        ) : (
          <MessageItem key={entry.message.id} conversationId={conversationId} message={entry.message} />
        ),
      )}
      {waitingForRunner ? (
        <div className="flex items-center gap-2 text-sm text-ink-3">
          <span
            className="h-1.5 w-1.5 animate-pulse rounded-full bg-signal"
            aria-hidden
          />
          Waiting for a runner to pick this up…
        </div>
      ) : null}
      <div ref={bottomRef} />
    </div>
  )
}
